import { TFunction } from "i18next";
import { PageBlockDto } from "~/modules/pageBlocks/dtos/PageBlockDto";
import { defaultFooter } from "../defaultFooter";
import { defaultHeader } from "../defaultHeader";
import Constants from "~/application/Constants";
import { defaultTestimonials } from "../defaultTestimonials";
import { defaultGallery } from "../defaultGallery";
import { defaultFaq } from "../defaultFaq";

export function defaultLandingPage({ t }: { t: TFunction }) {
  const blocks: PageBlockDto[] = [
    // Banner
    {
      banner: {
        style: "top",
        text: "Stop juggling multiple QuickBooks accounts manually. Lunova gives bookkeepers real-time alerts across all client books — so nothing slips through the cracks.",
        textMd: "",
        cta: [
          {
            text: "Start Free",
            href: "/register",
            isPrimary: true,
            target: "_blank",
          },
        ],
      },
    },
    // Header
    { header: defaultHeader({ t }) },
    // Hero
    {
      hero: {
        style: "rightImage",
        headline: "Never Miss a Critical Update Across All Your Client Books Again",
        description: "Stop logging into multiple QuickBooks accounts to check for changes. Lunova monitors all your clients' books in real-time and alerts you instantly when deposits, invoices, bills, or payments need attention.",
        secondaryBadgeUrl: `${process.env.SUPABASE_API_URL}/storage/v1/object/public/public-image//quickbook%20badge.png`,
        image: `${process.env.SUPABASE_API_URL}/storage/v1/object/public/public-image//lunova%20hero%20landing%20page.png`,
        cta: [
          {
            text: "Try it for free",
            href: "/register",
            isPrimary: true,
          },
          {
            text: "Watch Demo",
            href: "#demo",
            isPrimary: false,
          },
        ],
        topText: {
          text: "Built for bookkeepers who manage multiple QuickBooks clients",
        }
      },
    },
    {
      video: {
        style: "full",
        headline: "See How Lunova Works for Bookkeepers",
        src: "https://app.supademo.com/embed/cmd5a1pqj556hc4kjg9pba6j6?embed_v=2",
      },
    },
    // {
    //   video: {
    //     style: "full",
    //     headline: "See How Lunova Works for Bookkeepers",
    //     src: "https://www.loom.com/embed/f4315d430e3541149aba200ceda4dc2d?sid=648ef383-8c23-474e-ba85-9bb6131bb607",
    //   },
    // },
    // How It Works Section (as features/cards)
    {
      heading: {
        style: "centered",
        headline: "How Lunova Works for Bookkeepers",
        subheadline: "Get set up in minutes, not hours",
      },
    },
    {
      features: {
        style: "cards",
        headline: "3 Simple Steps to Proactive Client Monitoring",
        grid: { columns: "3", gap: "md" },
        items: [
          {
            name: "Connect Clients",
            icon: "🔗",
            description: "Connect as many QuickBooks accounts as you need.",
          },
          {
            name: "Set Up Alerts",
            icon: "⚡",
            description: "Choose what to monitor: deposits, invoices, bills, payments. Set custom thresholds and notification preferences for each client.",
          },
          {
            name: "Get Instant Alerts",
            icon: "📣",
            description: "Receive real-time notifications across all client accounts. View all alerts in one dashboard and take action before issues become problems.",
          },
        ],
      },
    },
    {
      features: {
        style: "list",
        topText:
          "DESIGNED FOR BOOKKEEPERS WHO MANAGE MULTIPLE CLIENTS AND NEED TO STAY ON TOP OF EVERY FINANCIAL CHANGE — WITHOUT CONSTANT LOGINS.",
        headline: "What Lunova Does for Bookkeepers",
        cta: [
          { text: "Try it for free", isPrimary: true, href: "/register" },
          { text: "View Pricing", isPrimary: false, href: "/pricing" },
        ],
        grid: { columns: "5", gap: "sm" },
        items: [
          { name: "Monitor all client QuickBooks accounts from one dashboard — no more constant logins." },
          { name: "Get instant alerts when deposits, invoices, or bills need attention across all clients." },
          { name: "Never miss overdue invoices or unexpected vendor price increases again." },
          {
            name: "Save 5-10 hours per week by eliminating manual checking and spreadsheet tracking.",
          },
          { name: "Look more professional to clients with proactive financial monitoring." },
        ],
      },
    },
    {
      features: {
        style: "cards",
        headline: "Monitor Every QuickBooks Entity That Matters",
        subheadline: "Set up custom alerts for exactly what you need to track",
        position: "center",
        grid: { columns: "3", gap: "sm" },
        items: [
          {
            name: "Deposits",
            description: "Track when payments hit bank accounts and payment methods used",
            icon: `<svg … ></svg>`,
            link: {
              text: "Learn More",
              href: "https://uselunova.com/docs/articles/alert-type-deposit",
            },
          },
          {
            name: "Invoices",
            description: "Monitor overdue invoices and payment status changes",
            icon: `<svg … ></svg>`,
            link: {
              text: "Learn More",
              href: "https://uselunova.com/docs/articles/alert-type-invoice",
            },
          },
          {
            name: "Bills",
            description: "Track vendor bill increases and payment schedules",
            icon: `<svg … ></svg>`,
            link: {
              text: "Learn More",
              href: "https://uselunova.com/docs/articles/alert-type-bill",
            },
          },
          {
            name: "Accounts",
            description: "Monitor account balance changes and classifications",
            link: {
              text: "Learn More",
              href: "https://uselunova.com/docs/articles/alert-type-account",
            },
          },
          {
            name: "Payments",
            description: "Track large incoming cash and AR payment changes",
            link: {
              text: "Learn More",
              href: "https://uselunova.com/docs/articles/alert-type-payment",
            },
          },
          {
            name: "Products",
            description: "Monitor product-related changes and inventory updates",
            link: {
              text: "Learn More",
              href: "https://uselunova.com/docs/articles/alert-type-product",
            },
          },
        ],
      },
    },
    // Gallery
    {
      gallery: {
        style: "carousel",
        items: defaultGallery,
      },
    },
    {
      testimonials: {
        style: "simple",
        headline: "Bookkeepers Who Trust Lunova to Monitor Their Client Books",
        items: [
          {
            role: "Bookkeeper",
            company: "Shoreline Design Studio",
            name: "Tina R.",
            personalWebsite: "",
            avatar: "",
            title: "Alerts tailored to *exactly* what I need to track",
            quote:
              "I set up custom alerts for high-value bills, overdue invoices, and deposit patterns across all my clients. It's granular, easy to use, and helps me stay on top of my clients' books without constant digging through reports. I've saved at least 8 hours per week.",
            quoteUrl: "",
            createdAt: new Date("2025-04-09T08:10:00.000Z"),
            stars: 5,
          },
          {
            role: "Solo Bookkeeper",
            company: "Multiple Small Businesses",
            name: "Maria L.",
            personalWebsite: "",
            avatar: "",
            title: "No more juggling 15 different QuickBooks logins",
            quote:
              "I manage 15 different client accounts and was constantly logging in to check for changes. Lunova gives me one dashboard that monitors everything. I get alerts instantly when something needs attention, and my clients think I'm psychic because I catch issues so quickly.",
            quoteUrl: "",
            createdAt: new Date("2025-03-15T14:30:00.000Z"),
            stars: 5,
          },
          {
            role: "Bookkeeping Firm Owner",
            company: "Precision Books Co.",
            name: "David K.",
            personalWebsite: "",
            avatar: "",
            title: "Scaled from 8 to 25 clients without adding staff",
            quote:
              "Lunova helped us scale our practice efficiently. We monitor all client books proactively, catch issues before clients notice, and look incredibly professional. It's been a game-changer for our 3-person firm.",
            quoteUrl: "",
            createdAt: new Date("2025-02-20T11:45:00.000Z"),
            stars: 5,
          },
          {
            role: "Part-time Bookkeeper",
            company: "Various Small Businesses",
            name: "Sarah M.",
            personalWebsite: "",
            avatar: "",
            title: "Finally stopped using spreadsheets to track everything",
            quote:
              "I was using Google Sheets with custom scripts to track client payments and bills. It was fragile and time-consuming. Lunova replaced all that with reliable, real-time monitoring. I can focus on actual bookkeeping instead of tracking.",
            quoteUrl: "",
            createdAt: new Date("2025-03-08T09:20:00.000Z"),
            stars: 5,
          },
          {
            role: "Remote Bookkeeper",
            company: "Freelance Clients",
            name: "James T.",
            personalWebsite: "",
            avatar: "",
            title: "Peace of mind knowing I won't miss anything important",
            quote:
              "Working remotely with multiple clients, I was always worried about missing critical updates. Lunova gives me confidence that I'll catch everything important across all my client accounts. It's like having a reliable assistant who never sleeps.",
            quoteUrl: "",
            createdAt: new Date("2025-04-12T16:15:00.000Z"),
            stars: 5,
          },
        ],
      },
    },
    // Community Section (remove unsupported 'items', just use headline/subheadline)
    {
      community: {
        style: "simple",
        headline: "Join the Bookkeeper Community",
        subheadline: "Connect with other bookkeepers who are using Lunova to scale their practices",
      },
    },
    // Pricing
    {
      pricing: {
        style: "simple",
        headline: "Simple Pricing for Bookkeepers",
        subheadline: "Start free, scale as you grow your practice",
        allowCoupons: true,
        contactUs: {
          title: "Need a custom plan?",
          description: "Contact us for enterprise pricing if you manage 50+ client accounts",
          features: ["Unlimited client accounts", "Priority support", "Custom integrations"],
        },
      },
    },
    {
      faq: {
        style: "simple",
        headline: "Frequently Asked Questions",
        items: [
          {
            question: "How does Lunova work with multiple QuickBooks accounts?",
            answer:
              "Lunova connects to all your clients' QuickBooks Online accounts through secure API integration. You set up custom alerts for each client, and Lunova monitors all accounts simultaneously from one dashboard. No more logging into multiple accounts to check for changes.",
          },
          {
            question: "What types of alerts can I set up for my clients?",
            answer:
              "You can create alerts for deposits, invoices, bills, payments, accounts, and products. Set custom thresholds like 'alert me when any invoice over $5,000 is overdue' or 'notify me when vendor bills increase by more than 20%'. Each alert is fully customizable to your specific needs.",
          },
          {
            question: "Will Lunova modify my clients' QuickBooks data?",
            answer:
              "No, Lunova is read-only. We only monitor your clients' QuickBooks data and send you alerts based on the conditions you set. We never modify, update, delete, or alter any QuickBooks data.",
          },
          {
            question: "How many client accounts can I monitor?",
            answer:
              "Our Free plan includes 2 alerts to get you started. Our Basic plan ($29/month) supports up to 10 client accounts, and our Pro plan ($79/month) supports unlimited client accounts with advanced features.",
          },
          {
            question: "How do I connect my clients' QuickBooks accounts?",
            answer:
              "Each client will need to authorize Lunova to access their QuickBooks Online account. You can send them a secure invitation link, or they can connect directly through your Lunova dashboard. The process takes just a few minutes per client.",
          },
          {
            question: "What if my clients are concerned about data security?",
            answer:
              "Lunova uses bank-level security with read-only access to QuickBooks. We're SOC 2 compliant, GDPR compliant, and never store sensitive financial data. Your clients' data remains in their QuickBooks account - we only access it to monitor for your specified conditions.",
          },
          {
            question: "Can I set different alerts for different clients?",
            answer:
              "Absolutely! Each client can have their own custom alert rules. You might set high-value invoice alerts for one client, vendor bill monitoring for another, and deposit tracking for a third. All alerts are managed from your central dashboard.",
          },
          {
            question: "How much time will this save me?",
            answer:
              "Most bookkeepers save 5-10 hours per week by eliminating manual checking of multiple QuickBooks accounts. Instead of logging into 15 different accounts daily, you get instant alerts when something needs attention. This time savings allows you to take on more clients or focus on higher-value work.",
          },
          {
            question: "What support do you offer for bookkeepers?",
            answer:
              "We offer email support during business hours for all plans. Pro plan subscribers get priority support and chat access. We also provide detailed documentation and video tutorials specifically designed for bookkeepers managing multiple client accounts.",
          },
        ],
      },
    },
    {
      blogPosts: {
        style: "simple",
        withCoverImage: true,
        withAuthorName: true,
        withAuthorAvatar: true,
        withDate: true,
        blogPath: "/blog",
        data: null,
      },
    },
    // Footer
    {
      footer: defaultFooter({ t }),
    },
  ];
  return blocks;
}
